import { PLACE_DETAIL_FIELDS, PlaceDetails, normalizePlaceDetails } from './placeDetails';

export interface LocationRecommendation {
  name: string;
  place_id: string;
}

export const normalizePlaceId = (placeId?: string | null): string => {
  if (!placeId) return '';
  const trimmed = String(placeId).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('places/')) return trimmed.slice(7);
  return trimmed;
};

export const normalizeLocationRecommendations = (
  recommendations?: Array<Record<string, unknown>> | null,
): LocationRecommendation[] => {
  if (!Array.isArray(recommendations)) return [];

  return recommendations
    .map((item) => ({
      name: String(item.name || item.title || '').trim(),
      place_id: normalizePlaceId(
        (item.place_id || item.placeId || item.google_place_id) as string | undefined,
      ),
    }))
    .filter((item) => item.name);
};

const looksLikeGooglePlaceId = (placeId: string) =>
  /^[A-Za-z0-9_-]{10,}$/.test(placeId) && !/\s/.test(placeId);

const hasLegacyPlacesService = (): boolean =>
  Boolean(window.google?.maps?.places?.PlacesService);

let placesLibraryLoadPromise: Promise<boolean> | null = null;

const wait = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

export const ensurePlacesLibraryLoaded = async (): Promise<boolean> => {
  if (hasLegacyPlacesService()) {
    return true;
  }

  if (placesLibraryLoadPromise) {
    return placesLibraryLoadPromise;
  }

  placesLibraryLoadPromise = (async () => {
    for (let attempt = 0; attempt < 50; attempt += 1) {
      if (window.google?.maps?.importLibrary) {
        try {
          await window.google.maps.importLibrary('places');
          return true;
        } catch {
          if (hasLegacyPlacesService()) {
            return true;
          }
        }
      } else if (hasLegacyPlacesService()) {
        return true;
      }

      await wait(200);
    }

    return hasLegacyPlacesService();
  })();

  const loaded = await placesLibraryLoadPromise;
  if (!loaded) {
    placesLibraryLoadPromise = null;
  }

  return loaded;
};

let placesServiceHost: HTMLDivElement | null = null;

const getPlacesServiceHost = (): HTMLElement => {
  if (!placesServiceHost) {
    placesServiceHost = document.createElement('div');
    placesServiceHost.style.display = 'none';
    document.body.appendChild(placesServiceHost);
  }
  return placesServiceHost;
};

const getPlacesServiceElement = (mapElement: HTMLElement | null): HTMLElement =>
  mapElement || getPlacesServiceHost();

const NEW_PLACE_FIELDS = [
  'displayName',
  'formattedAddress',
  'location',
  'rating',
  'userRatingCount',
  'nationalPhoneNumber',
  'internationalPhoneNumber',
  'websiteURI',
  'googleMapsURI',
  'regularOpeningHours',
  'photos',
  'priceLevel',
  'types',
  'businessStatus',
  'editorialSummary',
];

const mapNewPlaceToDetails = (place: Record<string, any>): PlaceDetails => {
  const photos = (place.photos || []).map((photo: Record<string, any>) => ({
    getUrl: (options: { maxWidth?: number; maxHeight?: number }) =>
      photo.getURI?.({ maxWidth: options.maxWidth, maxHeight: options.maxHeight }) || '',
  }));

  const location = place.location;
  const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
  const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;

  return normalizePlaceDetails({
    name: place.displayName?.text || place.displayName || '',
    formatted_address: place.formattedAddress,
    rating: place.rating,
    user_ratings_total: place.userRatingCount,
    formatted_phone_number: place.nationalPhoneNumber,
    international_phone_number: place.internationalPhoneNumber,
    website: place.websiteURI,
    url: place.googleMapsURI,
    price_level: place.priceLevel,
    types: place.types,
    business_status: place.businessStatus,
    editorial_summary: place.editorialSummary?.text
      ? { overview: place.editorialSummary.text }
      : place.editorialSummary,
    opening_hours: place.regularOpeningHours
      ? {
          open_now: place.regularOpeningHours.openNow,
          weekday_text: place.regularOpeningHours.weekdayDescriptions,
        }
      : undefined,
    photos,
    geometry: lat !== undefined && lng !== undefined
      ? {
          location: {
            lat: () => lat,
            lng: () => lng,
          },
        }
      : undefined,
  });
};

const getPlacesLibrary = async (): Promise<Record<string, any> | null> => {
  const loaded = await ensurePlacesLibraryLoaded();
  if (!loaded || !window.google?.maps?.importLibrary) {
    return null;
  }

  try {
    return await window.google.maps.importLibrary('places') as Record<string, any>;
  } catch {
    return null;
  }
};

const resolvePlaceIdFromNewApi = async (
  query: string,
  locationBias?: { lat: number; lng: number },
): Promise<string | null> => {
  const placesLibrary = await getPlacesLibrary();
  const Place = placesLibrary?.Place;
  if (!Place?.searchByText) return null;

  try {
    const request: Record<string, unknown> = {
      textQuery: query,
      fields: ['id', 'displayName'],
      maxResultCount: 1,
    };

    if (locationBias) {
      request.locationBias = locationBias;
    }

    const { places } = await Place.searchByText(request);
    const firstPlace = places?.[0];
    const resolvedId = normalizePlaceId(firstPlace?.id);
    return resolvedId || null;
  } catch {
    return null;
  }
};

const resolvePlaceIdFromQuery = async (
  mapElement: HTMLElement | null,
  query: string,
  locationBias?: { lat: number; lng: number },
): Promise<string | null> => {
  const fromNewApi = await resolvePlaceIdFromNewApi(query, locationBias);
  if (fromNewApi) return fromNewApi;

  const placesReady = await ensurePlacesLibraryLoaded();
  if (!placesReady || !hasLegacyPlacesService()) return null;

  return new Promise((resolve) => {
    const service = new window.google.maps.places.PlacesService(getPlacesServiceElement(mapElement));
    service.findPlaceFromQuery(
      {
        query,
        fields: ['place_id', 'name'],
        locationBias: locationBias
          ? {
              center: locationBias,
              radius: 50000,
            }
          : undefined,
      },
      (results: Array<{ place_id?: string }> | null, status: string) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results?.[0]?.place_id) {
          resolve(null);
          return;
        }
        resolve(results[0].place_id!);
      },
    );
  });
};

const fetchWithLegacyPlacesService = async (
  mapElement: HTMLElement | null,
  placeId: string,
): Promise<PlaceDetails | null> => {
  const placesReady = await ensurePlacesLibraryLoaded();
  if (!placesReady || !hasLegacyPlacesService()) return null;

  return new Promise((resolve) => {
    const service = new window.google.maps.places.PlacesService(getPlacesServiceElement(mapElement));
    service.getDetails(
      {
        placeId,
        fields: PLACE_DETAIL_FIELDS,
      },
      (place: PlaceDetails | null, status: string) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }

        resolve(
          normalizePlaceDetails(
            place as PlaceDetails & { current_opening_hours?: PlaceDetails['opening_hours'] },
          ),
        );
      },
    );
  });
};

const fetchWithNewPlacesApi = async (placeId: string): Promise<PlaceDetails | null> => {
  const placesLibrary = await getPlacesLibrary();
  const Place = placesLibrary?.Place;
  if (!Place) return null;

  try {
    const place = new Place({ id: placeId });
    await place.fetchFields({ fields: NEW_PLACE_FIELDS });
    const details = mapNewPlaceToDetails(place as unknown as Record<string, any>);
    return details.name ? details : null;
  } catch {
    return null;
  }
};

export interface PlaceSearchResult {
  id: string;
  name: string;
  address?: string;
  rating?: number;
  // Coordinates, when Google returns them — lets the picker drop the result as a
  // tappable pin on the day map so nearby options are visible at a glance.
  location?: { lat: number; lng: number };
}

/**
 * Text search for the add-activity place picker (REQ-138). Returns candidates
 * with their real place_ids and coordinates; `contextText` (trip destinations)
 * biases ambiguous queries toward the trip's area, and `locationBias` (usually
 * the map's current centre) pulls results toward what the user is looking at so
 * the map fills with nearby places to choose from.
 */
export const searchPlacesByText = async (
  query: string,
  contextText?: string,
  locationBias?: { lat: number; lng: number },
): Promise<PlaceSearchResult[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const placesLibrary = await getPlacesLibrary();
  const Place = placesLibrary?.Place;
  if (!Place?.searchByText) return [];

  try {
    const textQuery = contextText ? `${trimmed}, ${contextText}` : trimmed;
    const request: Record<string, any> = {
      textQuery,
      fields: ['id', 'displayName', 'formattedAddress', 'rating', 'location'],
      maxResultCount: 12,
    };
    if (locationBias) {
      request.locationBias = { center: locationBias, radius: 4000 };
    }
    const { places } = await Place.searchByText(request);
    return (places || [])
      .map((place: Record<string, any>) => {
        const loc = place.location;
        const lat = typeof loc?.lat === 'function' ? loc.lat() : loc?.lat;
        const lng = typeof loc?.lng === 'function' ? loc.lng() : loc?.lng;
        return {
          id: normalizePlaceId(place.id),
          name: String(place.displayName?.text || place.displayName || '').trim(),
          address: place.formattedAddress || undefined,
          rating: typeof place.rating === 'number' ? place.rating : undefined,
          location:
            typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : undefined,
        };
      })
      .filter((item: PlaceSearchResult) => item.id && item.name);
  } catch {
    return [];
  }
};

export const resolvePlaceIdFromGoogle = (
  mapElement: HTMLElement | null,
  query: string,
  locationBias?: { lat: number; lng: number },
): Promise<string | null> => resolvePlaceIdFromQuery(mapElement, query, locationBias);

export const fetchPlaceDetailsFromGoogle = async (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<PlaceDetails | null> => {
  const placesReady = await ensurePlacesLibraryLoaded();
  if (!placesReady) return null;

  const normalizedId = normalizePlaceId(placeId);
  const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';

  if (candidateId) {
    const fromNewApi = await fetchWithNewPlacesApi(candidateId);
    if (fromNewApi) return fromNewApi;

    const fromLegacyApi = await fetchWithLegacyPlacesService(mapElement, candidateId);
    if (fromLegacyApi) return fromLegacyApi;
  }

  const query = options?.queryFallback?.trim() || (!candidateId ? normalizedId : '');
  if (!query) return null;

  const resolvedId = await resolvePlaceIdFromQuery(mapElement, query, options?.locationBias);
  if (!resolvedId) return null;

  return fetchPlaceDetailsFromGoogle(mapElement, resolvedId, options);
};

const fetchPlaceGeometryUncached = async (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null> => {
  const placesReady = await ensurePlacesLibraryLoaded();
  if (!placesReady) return null;

  const normalizedId = normalizePlaceId(placeId);
  const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';

  if (candidateId) {
    const fromNewApi = await fetchWithNewPlacesApi(candidateId);
    const location = fromNewApi ? getPlaceLocation(fromNewApi) : null;
    if (location) {
      return { ...location, resolvedPlaceId: candidateId };
    }

    const fromLegacyApi = await fetchWithLegacyPlacesService(mapElement, candidateId);
    const legacyLocation = fromLegacyApi ? getPlaceLocation(fromLegacyApi) : null;
    if (legacyLocation) {
      return { ...legacyLocation, resolvedPlaceId: candidateId };
    }
  }

  const query = options?.queryFallback?.trim() || (!candidateId ? normalizedId : '');
  if (!query) return null;

  const resolvedId = await resolvePlaceIdFromQuery(mapElement, query, options?.locationBias);
  if (!resolvedId) return null;

  const details = await fetchPlaceDetailsFromGoogle(mapElement, resolvedId, options);
  const resolvedLocation = details ? getPlaceLocation(details) : null;
  if (!resolvedLocation) return null;

  return {
    ...resolvedLocation,
    resolvedPlaceId: resolvedId,
  };
};

// Session cache: a stop's geometry (place_id → lat/lng) is stable, so resolve it
// once and reuse across every re-plot instead of re-hitting Places on each render
// (this is what was firing dozens of GetPlace XHRs). Caches the promise (dedupes
// concurrent callers); a null result is evicted so a later attempt can retry.
const geometryCache = new Map<string, Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null>>();

export const fetchPlaceGeometryFromGoogle = (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null> => {
  // place_id (exact) or the fallback query fully determines the geometry; the map
  // element and location bias don't change the resolved point, so leave them out.
  const cacheKey = `${normalizePlaceId(placeId)}|${options?.queryFallback?.trim() || ''}`;
  const cached = geometryCache.get(cacheKey);
  if (cached) return cached;
  const pending = fetchPlaceGeometryUncached(mapElement, placeId, options);
  geometryCache.set(cacheKey, pending);
  pending.then((res) => { if (!res) geometryCache.delete(cacheKey); }).catch(() => geometryCache.delete(cacheKey));
  return pending;
};

export const getPlaceLocation = (place: PlaceDetails): { lat: number; lng: number } | null => {
  const location = place.geometry?.location;
  if (!location) return null;
  return { lat: location.lat(), lng: location.lng() };
};
